"use client"
import { isBrowser } from "@local/utils/src/isBrowser"
import { startTransition, useEffect, useState } from "react"

interface Props {
  action?: string
  input?: string | null
  truncate?: boolean
  discount?: number
}

// Replace discount percentage in CTA string
// Supports both %%DISCOUNT%% placeholder and hardcoded percentages like "-60%" or "60% OFF"
const replaceDiscount = (text: string, discount?: number): string => {
  if (!discount) return text

  // Replace %%DISCOUNT%% placeholder
  let result = text.replace("%%DISCOUNT%%", discount.toString())

  // Replace hardcoded percentages like "-60%" or "60% OFF"
  // Match patterns: -60%, 60% (with optional preceding dash/hyphen)
  result = result.replace(/[-–]?\d+%/g, (match) => {
    const hasDash = match.startsWith("-") || match.startsWith("–")
    return hasDash ? `-${discount}%` : `${discount}%`
  })

  return result
}

// fallback in english, as we don't have access to the locale in the server
export const getInitialCtaString = (
  input?: string | null,
  action?: string,
  discount?: number
) => {
  if (!input) return "Buy now"

  const result = input
    .replace("%%DATE%%", "Today")
    .replace("%%ACTION%%", action || "Buy now")

  return replaceDiscount(result, discount)
}

export const CallToAction = ({ action, input, truncate, discount }: Props) => {
  //  const ctaText = callToAction && callToAction !== "" ? callToAction :  "Buy now"

  const [callToAction, setCallToAction] = useState<string>(
    getInitialCtaString(input, action || "Select size", discount)
  )

  const parseCallToAction = () => {
    if (isBrowser() && input && input.indexOf("%%DATE") > -1) {
      const date = new Date()?.toLocaleString("en-US", {
        day: "numeric",
        month: "short",
      })

      const data = input
        .replace("%%DATE%%", date)
        .replace("%%ACTION%%", action || "Select size")

      return replaceDiscount(data, discount)
    } else {
      const text = input ?? "Buy now"
      const result = text
        .replace("%%DATE%%", "Today")
        .replace("%%ACTION%%", action || "Buy now")

      return replaceDiscount(result, discount)
    }
  }

  useEffect(() => {
    startTransition(() => {
      setCallToAction(parseCallToAction()!)
    })
  })

  return (
    <span
      className={
        truncate
          ? "block w-full overflow-hidden text-ellipsis whitespace-nowrap md:whitespace-normal"
          : "block leading-5"
      }
    >
      {callToAction}
    </span>
  )
}

export default CallToAction
